import { Log, Logger } from "../Logger";
import { NewformaApiClient } from "./NewformaApiClient";
import { HttpRequestWrapper } from "../HttpRequestWrapper";
import { EmailListItem, EmailListResponse } from "../../models/EmailListResponse";
import { Request } from "aws-sign-web";
import { PostEmailFilingResponse } from "../../models/PostEmailFilingResponse";
import { AnalyticsActionType, AnalyticsCategoryType, AnalyticsManager } from "../AnalyticsManager";
import { OfficeRoamingSettings } from "../OfficeRoamingSettings";
import { ExpiredSessionError } from "../../models/ExpiredSessionError";
import { OfficeWrapper } from "../OfficeWrapper";
import { SmartFilingManager } from "../SmartFiling/SmartFilingManager";
import { ITag } from "office-ui-fabric-react";
import { FilingLocationsResponse } from "../../models/FilingLocationsResponse";
import pLimit from "p-limit";
import {
    EmailListItemWithSelectedStatus,
    ExtendedITag,
} from "../../components/fileMultipleEmail/FileMultipleEmailComponent";

export class EmailApiService {
    private readonly emailListPageSize = 50;
    private readonly maxSimultaneousFillingRequest = 10;

    private componentName: string = this.constructor.name;
    private _urlRoot: string = "";

    constructor(
        private logger: Logger,
        private newformaApiClient: NewformaApiClient,
        private requestWrapper: HttpRequestWrapper,
        private analyticsManager: AnalyticsManager,
        private officeRoamingSettings: OfficeRoamingSettings,
        private officeWrapper: OfficeWrapper,
        private smartFilingManager: SmartFilingManager
    ) {}

    get urlRoot() {
        if (this._urlRoot === "") {
            this._urlRoot = this.newformaApiClient.getHostNameWithProtocol();
        }
        return this._urlRoot;
    }

    async getEmails(offsetToken: string | null): Promise<EmailListResponse> {
        Log.info(`${this.componentName} getEmails`);
        const url = `${this.urlRoot}/v1/email/folders/inbox/messages`;

        const params = {
            pageSize: offsetToken ? this.emailListPageSize + 1 : this.emailListPageSize,
            offsetToken: `${offsetToken || ""}`,
        };
        const options: Request = {
            url: url,
            method: "GET",
            headers: {
                "x-newforma-agent": this.newformaApiClient.getNewformaAgent(),
            },
            params: params,
        };

        Log.info("Fetching Emails");
        return this.newformaApiClient.makeRequest(options, (signedOptions) =>
            this.requestWrapper.get(signedOptions.url, undefined, signedOptions.headers, params)
        );
    }

    async onFileEmailFormSubmit(
        selectedEmailIds: string[],
        selectedProject: ITag,
        projects: ExtendedITag<string>[],
        culture: string,
        fileConversation: boolean,
        deleteEmailAfterFiling: boolean,
        loadedEmails: EmailListItem[],
        selectedItem: ITag | null,
        setEmails: (emails: EmailListItemWithSelectedStatus[], loadedEmails?: EmailListItem[]) => void,
        setFailedIds: (failedIds: string[]) => void,
        analyticsManager: AnalyticsManager,
        emails: EmailListItemWithSelectedStatus[]
    ): Promise<any> {
        let numberOfSuccessfulEmails = 0;
        let numberOfFailedEmails = 0;
        let emailList = emails;
        let remainingEmails = [] as EmailListItemWithSelectedStatus[];
        let remainingLoadedEmails = [] as EmailListItem[];
        try {
            const chosenProject = projects.find((x) => x.key === selectedProject.key);
            const failedIds = await this.fileMultipleEmails(
                selectedEmailIds,
                selectedProject,
                fileConversation, // override as conversation filing is not needed
                chosenProject?.additionalValue,
                culture,
                selectedItem
            );
            numberOfFailedEmails = failedIds.length;
            const successfulIds = selectedEmailIds.filter((id) => !failedIds.includes(id));
            numberOfSuccessfulEmails = successfulIds.length;
            if (deleteEmailAfterFiling) {
                remainingEmails = emails.filter((email) => !successfulIds.includes(email.id));
                remainingLoadedEmails = loadedEmails.filter((email) => !successfulIds.includes(email.id));
                setEmails(remainingEmails, remainingLoadedEmails);
            } else {
                const allEmailsAfterSave = emails.map((email) =>
                    successfulIds.includes(email.id) ? { ...email, isFiled: true } : email
                );
                const allLoadedEmailsAfterSave = loadedEmails.map((email) =>
                    successfulIds.includes(email.id) ? { ...email, isFiled: true } : email
                );

                setEmails(allEmailsAfterSave, allLoadedEmailsAfterSave);
                emailList = allEmailsAfterSave;
            }
            if (failedIds.length) {
                setFailedIds(failedIds);
            }
            analyticsManager.recordEvent(AnalyticsCategoryType.UserActions, AnalyticsActionType.FileMultipleEmail);
        } catch (error) {
            this.logger.error("FileMultipleEmailComponent filing email error", error);
            return error;
        } finally {
            const uncheckedEmails = deleteEmailAfterFiling
                ? remainingEmails.map((email) => ({ ...email, isSelected: false }))
                : emailList.map((email) => ({ ...email, isSelected: false }));
            setEmails(uncheckedEmails);
        }
        return { numberOfSuccessfulEmails, numberOfFailedEmails };
    }

    async fileMultipleEmails(
        emailIds: string[],
        project: ITag,
        fileConversation?: boolean,
        autoFileMailboxFolderName?: string,
        culture?: string,
        selectedItem?: ITag | null,
        onEmailProcessed?: (emailId: string) => void,
        autoFileMailboxSubfolderName?: string
    ): Promise<string[]> {
        Log.info(`${this.componentName} fileMultipleEmails`);
        const limit = pLimit(this.maxSimultaneousFillingRequest);
        const postFilingAction = this.officeRoamingSettings.getPostFilingAction();

        const deleteAfterFiling = this.officeRoamingSettings.getDeleteEmailAfterFiling();
        this.logger.info(`** FileConversation : ${fileConversation}`);
        // Email filing still uses Lambda to expand conversation (currently incorrect),
        // File to Project Item uses addin code to correct expand conversation (npc-30617)
        const failedIds: string[] = [];
        const nrnPrefix = "nrn:email:message:ms-graph:";
        const fileEmailPromises = emailIds.map((emailId) => {
            return limit(async () => {
                try {
                    await this.makeFilingRequest(
                        `${nrnPrefix}${emailId}`,
                        // need selectedItem to modify to be submittal or rfi or action item nrn, otherwise file email nrn
                        selectedItem ? selectedItem.key : project.key,
                        postFilingAction,
                        !!fileConversation,
                        autoFileMailboxFolderName,
                        culture,
                        autoFileMailboxSubfolderName
                    );
                } catch (error) {
                    Log.error(`EmailApiService email with id ${emailId} failed to file`, error);
                    failedIds.push(emailId);
                    if (ExpiredSessionError.isInstanceOf(error)) {
                        throw error;
                    }
                } finally {
                    onEmailProcessed?.(emailId);
                }
            });
        });

        await Promise.all(fileEmailPromises);

        this.analyticsManager.recordEvent(
            AnalyticsCategoryType.EmailFiling,
            AnalyticsActionType.FileMultipleEmail,
            `number filed: ${emailIds.length.toString()}`
        );

        if (fileConversation) {
            this.analyticsManager.recordEvent(
                AnalyticsCategoryType.EmailFiling,
                AnalyticsActionType.FiledWithConversation
            );
        }
        if (deleteAfterFiling) {
            this.analyticsManager.recordEvent(AnalyticsCategoryType.EmailFiling, AnalyticsActionType.FiledWithDelete);
        }

        try {
            const projectAsAny = project as any;
            this.analyticsManager.recordSmartFilingEvents(projectAsAny.suggestedProject, projectAsAny.suggestionIndex);
            const mailboxItem = this.officeWrapper.currentContextItem;
            if (mailboxItem) {
                await this.smartFilingManager.addToFiledHistory(
                    project,
                    mailboxItem.conversationId,
                    mailboxItem.sender.emailAddress
                );
            }
        } catch (error) {
            Log.error("EmailApiService failed to update smart filing history", error);
        }

        return failedIds;
    }
    async fileToProject(projectNrn: string, messageNrn: string, culture?: string): Promise<PostEmailFilingResponse> {
        Log.info(`${this.componentName} fileToProject ${messageNrn}`);

        const postFilingAction = this.officeRoamingSettings.getPostFilingAction();
        const fileConversation = this.officeRoamingSettings.getFileEmailConversation();
        const deleteAfterFiling = this.officeRoamingSettings.getDeleteEmailAfterFiling();

        let result: any;
        try {
            result = await this.makeFilingRequest(
                messageNrn,
                projectNrn,
                postFilingAction,
                fileConversation,
                "",
                culture
            );
        } catch (error) {
            Log.error("Error:", error);
        }

        if (fileConversation) {
            this.analyticsManager.recordEvent(
                AnalyticsCategoryType.EmailFiling,
                AnalyticsActionType.FiledWithConversation
            );
        }
        if (deleteAfterFiling) {
            this.analyticsManager.recordEvent(AnalyticsCategoryType.EmailFiling, AnalyticsActionType.FiledWithDelete);
        }
        return result;
    }

    async getFilingLocations(): Promise<FilingLocationsResponse> {
        Log.info(`${this.componentName} getFilingLocations`);
        const url = `${this.urlRoot}/v1/email/filinglocations`;
        const options = {
            hostname: this.newformaApiClient.getHostName(),
            url: url,
            method: "GET",
            headers: {
                "x-newforma-agent": this.newformaApiClient.getNewformaAgent(),
            },
        };

        return this.newformaApiClient.makeRequest(options, async (signedOptions: Request) =>
            this.requestWrapper.get(url, undefined, signedOptions.headers, undefined)
        );
    }

    async markEmailForFiling(emailId: string, culture?: string): Promise<any> {
        Log.info(`${this.componentName}  MarkEmailForFiling`);
        const fileConversation = this.officeRoamingSettings.getFileEmailConversation();
        const payload = JSON.stringify({
            emailId,
            postFiling: {
                fileEntireConversation: fileConversation,
            },
            culture,
        });
        const url = `${this.urlRoot}/v1/email/filing/async`;

        const options: Request = {
            hostname: this.newformaApiClient.getHostName(),
            url: url,
            method: "POST",
            body: payload,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "x-newforma-agent": this.newformaApiClient.getNewformaAgent(),
            },
        };

        return this.newformaApiClient.makeRequest(options, async (signedOptions: Request) => {
            const result = await this.requestWrapper.post(url, undefined, signedOptions.headers, payload);

            if (fileConversation) {
                this.analyticsManager.recordEvent(
                    AnalyticsCategoryType.EmailFiling,
                    AnalyticsActionType.FiledWithConversation
                );
            }
            return result;
        });
    }

    private async makeFilingRequest(
        messageNrn: string,
        projectNrn: string,
        postFilingAction: string,
        fileConversation: boolean,
        autoFileMailboxFolderName?: string,
        culture?: string,
        autoFileMailboxSubfolderName?: string
    ): Promise<PostEmailFilingResponse> {
        Log.info(`${this.componentName} makeFilingRequest`);

        const payload = JSON.stringify({
            messageNrn,
            postFiling: {
                action: postFilingAction,
                fileEntireConversation: fileConversation,
            },
            autoFileMailboxFolderName,
            autoFileMailboxSubfolderName,
            culture,
        });

        const url = `${this.urlRoot}/v1/email/filinglocations/${projectNrn}`;
        const options = {
            hostname: this.newformaApiClient.getHostName(),
            url: url,
            method: "POST",
            body: payload,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "x-newforma-agent": this.newformaApiClient.getNewformaAgent(),
            },
        };

        return this.newformaApiClient.makeRequest(options, async (signedOptions) =>
            this.requestWrapper.post(url, undefined, signedOptions.headers, payload)
        );
    }
}
